import { Mail, Clock, Phone, Languages } from "lucide-react";
import Reveal from "@/components/Reveal";
import IntakeForm from "@/components/IntakeForm";
import { OFFICE_EMAIL, OFFICE_LOCATION, OFFICE_PHONE, OFFICE_PHONE_TEL, SITE_URL } from "@/data/content";
import { useSEO } from "@/lib/useSEO";

export default function Contact() {
  useSEO("Contact the Office | Smart Legal Solutions", "Confidential matter intake and professional contact for Smart Legal Solutions, Vijayawada, Andhra Pradesh.");

  return (
    <div data-testid="contact-page" className="bg-navy pt-32">
      <div className="mx-auto max-w-7xl px-5 pb-28 lg:px-10">
        <Reveal>
          <p className="mono-label">Professional Contact</p>
          <h1 className="mt-4 max-w-3xl font-serif text-4xl font-medium leading-[1.1] tracking-tight text-ivory sm:text-5xl lg:text-6xl">
            Contact the Office
          </h1>
          <div className="gold-hairline mt-8" />
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-ivory/55">
            For professional communication, provide only the preliminary information necessary to identify the nature
            and procedural stage of the matter.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-px bg-ivory/10 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: Phone, label: "Telephone", value: OFFICE_PHONE, href: `tel:${OFFICE_PHONE_TEL}`, testid: "contact-phone-card" },
            { icon: Mail, label: "Email", value: OFFICE_EMAIL, href: `mailto:${OFFICE_EMAIL}`, testid: "contact-email-card" },
            { icon: Clock, label: "Office Hours", value: "By prior appointment", testid: "contact-hours-card" },
            { icon: Languages, label: "Languages", value: "English · Telugu · Hindi", testid: "contact-languages-card" },
          ].map((c) => (
            <Reveal key={c.label} className="bg-navy">
              <div data-testid={c.testid} className="flex h-full flex-col gap-4 p-7">
                <c.icon size={18} className="text-gold" />
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate">{c.label}</p>
                {c.href ? (
                  <a href={c.href} className="break-all text-sm text-ivory/80 transition-colors hover:text-gold">{c.value}</a>
                ) : (
                  <p className="text-sm text-ivory/80">{c.value}</p>
                )}
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-6">
          <div className="border border-ivory/10 bg-navy p-7" data-testid="contact-office-address">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate">Office</p>
            <p className="mt-3 font-serif text-xl text-ivory">Smart Legal Solutions</p>
            <p className="mt-1 text-sm text-ivory/60">{OFFICE_LOCATION}</p>
            <p className="mt-4 font-mono text-[10px] leading-relaxed tracking-[0.08em] text-slate">
              Consultations are held by prior appointment. A complete postal address is shared upon confirmation.
            </p>
          </div>
        </Reveal>

        <div id="matter-intake" className="scroll-mt-28 pt-20">
          <Reveal>
            <p className="mono-label">Step-by-Step</p>
            <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-ivory sm:text-4xl">
              Confidential Matter Intake
            </h2>
            <p className="mt-5 max-w-2xl text-sm leading-relaxed text-ivory/50">
              A private enquiry reference number is generated on submission. Do not submit confidential, privileged or
              highly sensitive information through this form. Submission does not by itself create an advocate-client
              relationship.
            </p>
            <p className="mt-4 text-sm text-ivory/50">
              Already submitted?{" "}
              <a href="/track" data-testid="contact-track-link" className="text-gold underline-offset-4 transition-colors hover:underline">
                Track your enquiry status
              </a>
            </p>
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <IntakeForm />
          </Reveal>
        </div>
      </div>
    </div>
  );
}
